import React from 'react';

export interface AdditionalInformationFormData {
  generalSummary: string;
  location: string;
  competition: string;
  expansionPotential: string;
  yearsEstablished: string;
  employees: string;
  tradingHours: string;
  supportTraining: string;
  relocatable: 'yes' | 'no' | '';
  workFromHome: boolean;
  franchise: boolean;
  assetSale: boolean;
  financingAvailable: boolean;
  leaseTerms: string;
  reasonForSale: string;
  askingPriceSpecific: string;
  salesRevenueSpecific: string;
  cashFlowSpecific: string;
  websiteAddress: string;
}

interface AdditionalInformationFormProps {
  formData: AdditionalInformationFormData;
  onInputChange: (field: keyof AdditionalInformationFormData, value: any) => void;
  uploadedPhotos: string[];
  uploadedDocuments: string[];
  selectedFile: File | null;
  onFileSelect: (file: File | null) => void;
  onFileUpload: (type: 'photo' | 'document') => void;
  onRemovePhoto?: (index: number) => void;
  onRemoveDocument?: (index: number) => void;
  showIntroduction?: boolean;
}

const AdditionalInformationForm: React.FC<AdditionalInformationFormProps> = ({
  formData,
  onInputChange,
  uploadedPhotos,
  uploadedDocuments,
  selectedFile,
  onFileSelect,
  onFileUpload,
  onRemovePhoto,
  onRemoveDocument,
  showIntroduction = true
}) => {
  return (
    <div className="space-y-8">
      {/* Introduction */}
      {showIntroduction && (
        <div className="bg-gradient-to-r from-slate-50 via-gray-50 to-slate-50 p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-gray-700 rounded-lg flex items-center justify-center shadow-md flex-shrink-0">
              <span className="text-white text-xl">📋</span>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">Additional Details</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Please provide further information on your business for sale. You may enter as much or as little information as you wish. You may go on to the next stage and return at a later date to enter this information.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* General Summary */}
      <div id="section-general-summary" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-5">
          <div className="w-1 h-6 bg-gradient-to-b from-slate-600 to-gray-700 rounded-full mr-3"></div>
          <h3 className="text-lg font-semibold text-gray-900">General Summary</h3>
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            General Summary
          </label>
          <textarea
            value={formData.generalSummary}
            onChange={(e) => onInputChange('generalSummary', e.target.value)}
            rows={6}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
            placeholder="Highlight the selling points of the business for sale..."
          />
          <p className="text-xs text-gray-600 mt-2">
            Highlight the selling points of the business for sale. Say as much or as little as you want. Please do not add phone numbers or email addresses to the description. We will remove them as they cannot be tracked.
          </p>
        </div>
      </div>

      {/* Photographs */}
      <div id="section-photographs" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Photographs</h3>
        
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3">Upload Photos</h4>
          <div className="flex items-center gap-4">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => onFileSelect(e.target.files?.[0] || null)}
              className="text-sm"
            />
            <button
              onClick={() => onFileUpload('photo')}
              disabled={!selectedFile}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
            >
              Upload
            </button>
          </div>
          {uploadedPhotos.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Uploaded Photos ({uploadedPhotos.length}/15)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {uploadedPhotos.map((photoUrl, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-primary-500 transition-colors">
                      <img
                        src={photoUrl}
                        alt={`Uploaded photo ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback if image fails to load
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                    {onRemovePhoto && (
                      <button
                        onClick={() => onRemovePhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove photo"
                        type="button"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-gray-600 mt-2">
            <strong>Important:</strong> Do not upload images without permission or which infringe on someone else's copyright. You can upload up to 15 images. Adverts with images and photographs receive more responses. Any photographs which reveal the name and location of your business will not be displayed until you buy an advertising package. The picture format must be a jpg, gif, png, tiff or bmp. Refrain from using excessively large images (over 10MB).
          </p>
        </div>
      </div>

      {/* Documents */}
      <div id="section-documents" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents</h3>
        
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3">Upload Documents</h4>
          <div className="flex items-center gap-4">
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              onChange={(e) => onFileSelect(e.target.files?.[0] || null)}
              className="text-sm"
            />
            <button
              onClick={() => onFileUpload('document')}
              disabled={!selectedFile}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
            >
              Upload
            </button>
          </div>
          {uploadedDocuments.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Uploaded Documents ({uploadedDocuments.length}/3)</p>
              <div className="space-y-2">
                {uploadedDocuments.map((docUrl, index) => {
                  // Extract filename from URL
                  const fileName = docUrl.split('/').pop()?.split('?')[0] || `Document ${index + 1}`;
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary-300 transition-colors group">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <a
                          href={docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-gray-700 hover:text-primary-600 truncate flex-1"
                          title={fileName}
                        >
                          {fileName}
                        </a>
                      </div>
                      {onRemoveDocument && (
                        <button
                          onClick={() => onRemoveDocument(index)}
                          className="ml-2 flex-shrink-0 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove document"
                          type="button"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <p className="text-xs text-gray-600 mt-2">
            You can upload up to 3 documents, including PDF, Word, Excel and other document types. Browse for documents on your hard drive to add additional information about the business for sale such as financials, business plans, presentations, company brochures etc.
          </p>
        </div>
      </div>

      {/* Property and Financial Details */}
      <div id="section-financial" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-5">
          <div className="w-1 h-6 bg-gradient-to-b from-slate-600 to-gray-700 rounded-full mr-3"></div>
          <h3 className="text-lg font-semibold text-gray-900">Property and Financial Details</h3>
        </div>
        
        <div className="space-y-8">
          {/* Asking Price */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Asking Price *
            </label>
            <div className="relative">
              <span className="absolute left-4 top-3 text-gray-500">$</span>
              <input
                type="number"
                value={formData.askingPriceSpecific}
                onChange={(e) => onInputChange('askingPriceSpecific', e.target.value)}
                className="w-full pl-10 pr-20 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
                placeholder="0"
              />
              <span className="absolute right-4 top-3 text-gray-500 text-sm">(CAD)</span>
            </div>
          </div>

          {/* Sales Revenue */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Sales Revenue *
            </label>
            <div className="relative">
              <span className="absolute left-4 top-3 text-gray-500">$</span>
              <input
                type="number"
                value={formData.salesRevenueSpecific}
                onChange={(e) => onInputChange('salesRevenueSpecific', e.target.value)}
                className="w-full pl-10 pr-24 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
                placeholder="0"
              />
              <span className="absolute right-4 top-3 text-gray-500 text-sm">(CAD) Annual</span>
            </div>
          </div>

          {/* Cash Flow */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Cash Flow *
            </label>
            <div className="relative">
              <span className="absolute left-4 top-3 text-gray-500">$</span>
              <input
                type="number"
                value={formData.cashFlowSpecific}
                onChange={(e) => onInputChange('cashFlowSpecific', e.target.value)}
                className="w-full pl-10 pr-16 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
                placeholder="0"
              />
              <span className="absolute right-4 top-3 text-gray-500 text-sm">(CAD)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Section A - Environment */}
      <div id="section-environment" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-5">
          <div className="w-1 h-6 bg-gradient-to-b from-slate-600 to-gray-700 rounded-full mr-3"></div>
          <h3 className="text-lg font-semibold text-gray-900">Section A - Environment</h3>
        </div>
        
        <div className="space-y-8">
          {/* Location */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Location
            </label>
            <textarea
              value={formData.location}
              onChange={(e) => onInputChange('location', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
              placeholder="Please describe the location of the business, e.g. prime high street location, trading estate, online, etc."
            />
          </div>

          {/* Competition */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Competition
            </label>
            <textarea
              value={formData.competition}
              onChange={(e) => onInputChange('competition', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
              placeholder="Please describe any competitive considerations that have faced the business or may face the business in the future."
            />
          </div>

          {/* Expansion Potential */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Expansion Potential
            </label>
            <textarea
              value={formData.expansionPotential}
              onChange={(e) => onInputChange('expansionPotential', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
              placeholder="Please describe any opportunities for expansion that exist or may be available in the future."
            />
          </div>
        </div>
      </div>

      {/* Section B - Operation */}
      <div id="section-operation" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-5">
          <div className="w-1 h-6 bg-gradient-to-b from-slate-600 to-gray-700 rounded-full mr-3"></div>
          <h3 className="text-lg font-semibold text-gray-900">Section B - Operation</h3>
        </div>
        
        <div className="space-y-6">
          {/* Years Established */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Years Established
            </label>
            <input
              type="text"
              value={formData.yearsEstablished}
              onChange={(e) => onInputChange('yearsEstablished', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
              placeholder="List the number of years the business has been established."
            />
          </div>

          {/* Employees */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employees
            </label>
            <input
              type="text"
              value={formData.employees}
              onChange={(e) => onInputChange('employees', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="List the number of employees the business currently employs."
            />
          </div>

          {/* Trading Hours */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trading Hours
            </label>
            <textarea
              value={formData.tradingHours}
              onChange={(e) => onInputChange('tradingHours', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
              placeholder="List the trading hours of the business, e.g. 24/7 operation, Mon-Sat, Mon-Fri business working hours, etc."
            />
          </div>

          {/* Support & Training */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Support & Training
            </label>
            <textarea
              value={formData.supportTraining}
              onChange={(e) => onInputChange('supportTraining', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
              placeholder="Describe any support or training that can be offered to a prospective buyer."
            />
          </div>
        </div>
      </div>

      {/* Section C - Terms */}
      <div id="section-terms" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-5">
          <div className="w-1 h-6 bg-gradient-to-b from-slate-600 to-gray-700 rounded-full mr-3"></div>
          <h3 className="text-lg font-semibold text-gray-900">Section C - Terms</h3>
        </div>
        
        <div className="space-y-6">
          {/* Relocatable */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Relocatable
            </label>
            <div className="space-y-2">
              {[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ].map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    name="relocatable"
                    value={option.value}
                    checked={formData.relocatable === option.value}
                    onChange={(e) => onInputChange('relocatable', e.target.value as 'yes' | 'no' | '')}
                    className="mr-2"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-1">Can the business be relocated?</p>
          </div>

          {/* Home Based */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.workFromHome}
                onChange={(e) => onInputChange('workFromHome', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Check this box if the business can be run from home.
              </span>
            </label>
          </div>

          {/* Franchise */}
          <div>
            <label className="flex items-center mb-2">
              <input
                type="checkbox"
                checked={formData.franchise}
                onChange={(e) => onInputChange('franchise', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Check this box if the business is a franchise resale.
              </span>
            </label>
          </div>

          {/* Business Closed or Asset Sale */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.assetSale}
                onChange={(e) => onInputChange('assetSale', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Check this box if the business is not operating and only the assets are being sold.
              </span>
            </label>
          </div>

          {/* Owner Financing Available */}
          <div>
            <label className="flex items-center mb-2">
              <input
                type="checkbox"
                checked={formData.financingAvailable}
                onChange={(e) => onInputChange('financingAvailable', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Willing to Seller Finance?
              </span>
            </label>
            <p className="text-xs text-gray-600 mb-3">
              Instead of borrowing all the capital from a bank or other finance provider, the buyer borrows all or part of the sale price from the seller. The buyer then repays the loan in instalments over a fixed period. Please state details of any arrangement you are willing to provide.
            </p>
            <textarea
              value={formData.leaseTerms}
              onChange={(e) => onInputChange('leaseTerms', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
              placeholder="If applicable, describe any available financing options to the buyer."
            />
          </div>

          {/* Reason For Selling */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason For Selling
            </label>
            <textarea
              value={formData.reasonForSale}
              onChange={(e) => onInputChange('reasonForSale', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
              placeholder="Please state the reason(s) for selling this business; e.g. retirement, ill health, business is non core etc."
            />
          </div>
        </div>
      </div>

      {/* Website Address */}
      <div id="section-website" className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-8 backdrop-blur-sm">
        <div className="flex items-center mb-6">
          <div className="w-2 h-8 bg-gradient-to-b from-purple-500 to-violet-600 rounded-full mr-4"></div>
          <h3 className="text-xl font-bold text-gray-900">Website Address</h3>
        </div>
        
        <div>
          <input
            type="url"
            value={formData.websiteAddress}
            onChange={(e) => onInputChange('websiteAddress', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 shadow-sm"
            placeholder="http://"
          />
          <p className="text-xs text-gray-600 mt-2">
            Add the website address of your business here. (Optional)
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdditionalInformationForm;
